import type { ApiErrorCode } from '@vlastiq/contracts';

/**
 * A non-2xx API response, carrying the server's machine-readable code.
 *
 * Its own module so that `telegram/auth.ts` can throw one without importing
 * `auth/api.ts`, which imports `telegram/auth.ts` back. The Mini App's token
 * exchange has to surface the SERVER's code — a member who is not in the group
 * must reach the not-a-member page, not a login page they cannot complete
 * inside a webview.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Parse an error body, falling back to something a member can act on. */
export async function toApiError(response: Response): Promise<ApiError> {
  let code: ApiErrorCode = 'internal';
  let message = '';
  try {
    const body = (await response.json()) as { error?: { code?: ApiErrorCode; message?: string } };
    code = body.error?.code ?? 'internal';
    message = body.error?.message ?? '';
  } catch {
    // Non-JSON error (proxy timeout, HTML error page) — fall through.
  }
  return new ApiError(
    response.status,
    code,
    // statusText is empty over HTTP/2, so fall through to something the user
    // can actually act on rather than rendering a blank message.
    message || response.statusText || `Запрос не выполнен (${response.status})`,
  );
}
